import { create } from 'zustand';
import type { Annotation, AnnotationStore } from '../types/annotation';

const STORAGE_KEY = 'claudafinil_annotations';
const MAX_MESSAGE_TEXT_LENGTH = 500;

function generateId(): string {
  return crypto.randomUUID();
}

function persistAnnotations(annotations: Annotation[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(annotations));
}

function loadAnnotationsFromStorage(): Annotation[] {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored ? JSON.parse(stored) : [];
}

const initialState = {
  annotations: [] as Annotation[],
  isLoading: false,
  error: null as string | null,
};

export const useAnnotationStore = create<AnnotationStore>((set, get) => ({
  ...initialState,

  loadAnnotations: async () => {
    set({ isLoading: true, error: null });

    try {
      const annotations = loadAnnotationsFromStorage();
      set({ annotations, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load annotations';
      set({ error: message, isLoading: false });
    }
  },

  addAnnotation: async ({ conversationId, conversationName, messageId, messageText, platform }) => {
    const now = Date.now();
    const newAnnotation: Annotation = {
      id: generateId(),
      conversationId,
      conversationName,
      messageId,
      messageText: messageText.slice(0, MAX_MESSAGE_TEXT_LENGTH),
      ...(platform ? { platform } : {}),
      note: '',
      createdAt: now,
      updatedAt: now,
    };

    const updatedAnnotations = [...get().annotations, newAnnotation];

    try {
      persistAnnotations(updatedAnnotations);
      set({ annotations: updatedAnnotations, error: null });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to add annotation';
      set({ error: message });
    }
  },

  removeAnnotation: async (id) => {
    const updatedAnnotations = get().annotations.filter((annotation) => annotation.id !== id);

    try {
      persistAnnotations(updatedAnnotations);
      set({ annotations: updatedAnnotations, error: null });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to remove annotation';
      set({ error: message });
    }
  },

  removeAnnotationByMessageId: async (messageId) => {
    const updatedAnnotations = get().annotations.filter(
      (annotation) => annotation.messageId !== messageId,
    );

    try {
      persistAnnotations(updatedAnnotations);
      set({ annotations: updatedAnnotations, error: null });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to remove annotation';
      set({ error: message });
    }
  },

  updateNote: async (id, note) => {
    const updatedAnnotations = get().annotations.map((annotation) =>
      annotation.id === id
        ? { ...annotation, note, updatedAt: Date.now() }
        : annotation,
    );

    try {
      persistAnnotations(updatedAnnotations);
      set({ annotations: updatedAnnotations, error: null });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update note';
      set({ error: message });
    }
  },

  getAnnotationByMessageId: (messageId) => {
    return get().annotations.find((annotation) => annotation.messageId === messageId);
  },
}));
